import React from "react";
import { Card, Col, Descriptions, Row, Space, Tag, Typography } from "antd";
import { useOwnDeliveryRecommendation } from "../../hooks/useOwnDeliveryRecommendation";
import { WarehouseToAddressMap } from "../Calculator/WarehouseToAddressMap";

export interface DeliveryAddressDistanceCardProps {
  deliveryInfo: {
    street_name?: string | null;
    house_number?: string | null;
    city?: string | null;
    postal_code?: string | null;
    country?: string | null;
    instructions?: string | null;
  };
}

/**
 * Booking delivery address summary with warehouse drive distance + route map.
 * Map is only shown when within the Own Delivery recommend radius (20 km).
 */
export const DeliveryAddressDistanceCard: React.FC<
  DeliveryAddressDistanceCardProps
> = ({ deliveryInfo }) => {
  const {
    distanceKm,
    loading,
    routeGeometry,
    destCoords,
    destLabel,
    error,
    isRecommended,
  } = useOwnDeliveryRecommendation({
    street_name: deliveryInfo.street_name || undefined,
    house_number: deliveryInfo.house_number || undefined,
    city: deliveryInfo.city || undefined,
    postal_code: deliveryInfo.postal_code || undefined,
    country: deliveryInfo.country || undefined,
  });

  const showMap = isRecommended && distanceKm != null;

  const distanceTag = loading ? (
    <Tag color="blue" style={{ margin: 0 }}>
      Calculating…
    </Tag>
  ) : distanceKm != null ? (
    <Tag color={showMap ? "green" : "blue"} style={{ margin: 0 }}>
      {Math.round(distanceKm)} km drive
    </Tag>
  ) : null;

  return (
    <Card
      title={
        <Space size={8}>
          <span>Delivery Address</span>
          {distanceTag}
        </Space>
      }
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} md={showMap ? 12 : 24}>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Street">
              {deliveryInfo.street_name || "N/A"}{" "}
              {deliveryInfo.house_number || ""}
            </Descriptions.Item>
            <Descriptions.Item label="City">
              {deliveryInfo.city || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Postal Code">
              {deliveryInfo.postal_code || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Country">
              {deliveryInfo.country || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Drive distance">
              {loading ? (
                <Typography.Text type="secondary">
                  Calculating from warehouse…
                </Typography.Text>
              ) : distanceKm != null ? (
                <Typography.Text>
                  {Math.round(distanceKm)} km from warehouse (Lindeboomseweg 57,
                  Amersfoort)
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">Unavailable</Typography.Text>
              )}
            </Descriptions.Item>
          </Descriptions>
          {deliveryInfo.instructions && (
            <Card
              title="Special Instructions"
              size="small"
              style={{ marginTop: 12 }}
            >
              <p style={{ margin: 0 }}>{deliveryInfo.instructions}</p>
            </Card>
          )}
        </Col>
        {showMap && (
          <Col xs={24} md={12}>
            <WarehouseToAddressMap
              title="Route from warehouse"
              compact
              destLabel={destLabel}
              distanceKm={distanceKm}
              destCoords={destCoords}
              routeGeometry={routeGeometry}
              loading={loading}
              error={error}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
};
